import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from '../hooks/use-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const { toast } = useToast();

  // Load stored authentication data on app start
  useEffect(() => {
    const storedToken = localStorage.getItem('itsmyvibe_token');
    const storedUser = localStorage.getItem('itsmyvibe_user');
    const storedKey = localStorage.getItem('itsmyvibe_encryption_key');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        const userData = JSON.parse(storedUser);
        
        // Check if user data has is_admin field, if not, refresh from server
        if (!userData.hasOwnProperty('is_admin')) {
          refreshUserData(storedToken);
        } else {
          setUser(userData);
        }
        
        if (storedKey) {
          setEncryptionKey(storedKey);
        }
      } catch (error) {
        console.error('Error loading stored auth data:', error);
        // Clear corrupted data
        localStorage.removeItem('itsmyvibe_token');
        localStorage.removeItem('itsmyvibe_user');
        localStorage.removeItem('itsmyvibe_encryption_key');
      }
    }
    setLoading(false);
  }, []);

  // Helper function to refresh user data from server
  const refreshUserData = async (token) => {
    try {
      const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
        localStorage.setItem('itsmyvibe_user', JSON.stringify(userData));
        console.log('User data refreshed with admin status:', userData.is_admin);
      } else {
        console.error('Failed to refresh user data');
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Set token first
      const token = data.access_token;
      setToken(token);
      localStorage.setItem('itsmyvibe_token', token);

      // Fetch complete user data from /api/me to get is_admin field
      const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
        localStorage.setItem('itsmyvibe_user', JSON.stringify(userData));
      } else {
        // Fallback to login response data if /api/me fails
        setUser(data);
        localStorage.setItem('itsmyvibe_user', JSON.stringify(data));
      }

      // Derive encryption key from password (client-side)
      const salt = await getSaltForUser(data.id);
      const derivedKey = await deriveEncryptionKey(password, salt);
      setEncryptionKey(derivedKey);
      localStorage.setItem('itsmyvibe_encryption_key', derivedKey);

      toast({
        title: "Success",
        description: "Successfully logged in!",
      });

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Error",
        description: error.message || "Login failed",
        variant: "destructive",
      });
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Set token first
      const token = data.access_token;
      setToken(token);
      localStorage.setItem('itsmyvibe_token', token);

      // Fetch complete user data from /api/me to get is_admin field
      const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
        localStorage.setItem('itsmyvibe_user', JSON.stringify(userData));
      } else {
        // Fallback to registration response data if /api/me fails
        setUser(data);
        localStorage.setItem('itsmyvibe_user', JSON.stringify(data));
      }

      // Derive encryption key from password (client-side)
      const salt = await getSaltForUser(data.id);
      const derivedKey = await deriveEncryptionKey(password, salt);
      setEncryptionKey(derivedKey);
      localStorage.setItem('itsmyvibe_encryption_key', derivedKey);

      toast({
        title: "Success",
        description: "Account created successfully!",
      });

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Error",
        description: error.message || "Registration failed",
        variant: "destructive",
      });
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setEncryptionKey(null);
    localStorage.removeItem('itsmyvibe_token');
    localStorage.removeItem('itsmyvibe_user');
    localStorage.removeItem('itsmyvibe_encryption_key');
    
    toast({
      title: "Success",
      description: "Successfully logged out!",
    });
  };

  // Helper function to get salt for a user (this would normally come from the backend)
  const getSaltForUser = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await response.json();
      return userData.salt || 'default-salt'; // Fallback salt
    } catch (error) {
      console.error('Error getting user salt:', error);
      return 'default-salt';
    }
  };

  // Client-side key derivation function
  const deriveEncryptionKey = async (password, salt) => {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);
    const saltBuffer = encoder.encode(salt);

    // Import password as key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive key using PBKDF2
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBuffer,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );

    // Export key as JWK for storage
    const exportedKey = await crypto.subtle.exportKey('jwk', key);
    return JSON.stringify(exportedKey);
  };

  const value = {
    user,
    token,
    encryptionKey,
    loading,
    login,
    register,
    logout,
    refreshUser: () => refreshUserData(token), // Expose refresh function
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};